import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllProcesses,
  getMyProcesses,
  createProcess,
  updateProcess,
  deleteProcess,
  searchProcesses,
  clearErrors,
  clearMessage,
  resetSuccess,
} from "../../redux/processSlice";
import { Helmet } from "react-helmet-async";

const Process = () => {
  const dispatch = useDispatch();
  const { processes, loading, error, success, message } = useSelector(
    (state) => state.process
  );
  const { user } = useSelector((state) => state.user);

  const [formData, setFormData] = useState({
    name: "",
    amount: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    amount: "",
  });

  const [touched, setTouched] = useState({
    name: false,
    amount: false,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showMyProcesses, setShowMyProcesses] = useState(false);

  // Validation functions
  const validateName = (name) => {
    if (!name || !name.trim()) {
      return "Process name is required";
    }
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      return "Process name must be at least 2 characters";
    }
    if (trimmed.length > 100) {
      return "Process name cannot exceed 100 characters";
    }
    return "";
  };

  const validateAmount = (amount) => {
    if (!amount && amount !== 0) {
      return "Amount is required";
    }
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum)) {
      return "Amount must be a valid number";
    }
    if (amountNum < 0) {
      return "Amount cannot be negative";
    }
    if (!Number.isFinite(amountNum)) {
      return "Amount must be a finite number";
    }
    return "";
  };

  const validateField = (field, value) => {
    let error = "";
    switch (field) {
      case "name":
        error = validateName(value);
        break;
      case "amount":
        error = validateAmount(value);
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [field]: error }));
    return error === "";
  };

  const validateForm = () => {
    const nameValid = validateField("name", formData.name);
    const amountValid = validateField("amount", formData.amount);
    setTouched({ name: true, amount: true });
    return nameValid && amountValid;
  };

  // Load processes on component mount
  useEffect(() => {
    if (showMyProcesses && user) {
      dispatch(getMyProcesses());
    } else {
      dispatch(getAllProcesses());
    }
  }, [dispatch, showMyProcesses, user]);

  // Clear messages after 3 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        dispatch(clearErrors());
        dispatch(clearMessage());
        dispatch(resetSuccess());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error, dispatch]);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "amount") {
      // Allow only numbers and decimal point
      const numericValue = value.replace(/[^0-9.]/g, "");
      // Prevent multiple decimal points
      const parts = numericValue.split(".");
      const finalValue = parts.length > 2 
        ? parts[0] + "." + parts.slice(1).join("")
        : numericValue;
      
      setFormData((prev) => ({ ...prev, [name]: finalValue }));
      if (touched[name]) {
        validateField(name, finalValue);
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (touched[name]) {
        validateField(name, value);
      }
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, formData[field]);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const processData = {
      name: formData.name.trim(),
      amount: parseFloat(formData.amount),
    };

    try {
      if (isEditing) {
        await dispatch(updateProcess({ id: editingId, processData })).unwrap();
      } else {
        await dispatch(createProcess(processData)).unwrap();
      }
      
      resetForm();
      setShowModal(false);
      if (showMyProcesses && user) {
        dispatch(getMyProcesses());
      } else {
        dispatch(getAllProcesses());
      }
    } catch (error) {
      // Error handled by Redux
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({ name: "", amount: "" });
    setErrors({ name: "", amount: "" });
    setTouched({ name: false, amount: false });
    setIsEditing(false);
    setEditingId(null);
  };

  // Handle edit
  const handleEdit = (process) => {
    setFormData({
      name: process.name,
      amount: process.amount.toString(),
    });
    setIsEditing(true);
    setEditingId(process._id);
    setShowModal(true);
    setErrors({ name: "", amount: "" });
    setTouched({ name: false, amount: false });
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this process?")) {
      try {
        await dispatch(deleteProcess(id)).unwrap();
        dispatch(getAllProcesses());
      } catch (error) {
        // Error handled by Redux
      }
    }
  };

  // Handle search
  const handleSearch = useCallback(
    async (query) => {
      setSearchQuery(query);
      if (query.trim()) {
        dispatch(searchProcesses(query));
      } else {
        if (showMyProcesses && user) {
          dispatch(getMyProcesses());
        } else {
          dispatch(getAllProcesses());
        }
      }
    },
    [dispatch, showMyProcesses, user]
  );

  // Toggle between all processes and my processes
  const handleToggleView = () => {
    setShowMyProcesses(!showMyProcesses);
    setSearchQuery("");
  };

  const showToast = useCallback((message, type = "success") => {
    const toast = document.createElement("div");
    toast.className = `process-toast process-toast-${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => toast.classList.add("process-toast-show"), 100);
    setTimeout(() => {
      toast.classList.remove("process-toast-show");
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 3000);
  }, []);

  useEffect(() => {
    if (success && message) {
      showToast(message, "success");
    }
    if (error) {
      showToast(error, "error");
    }
  }, [success, error, message, showToast]);

  return (
    <>
      <Helmet>
        <title>Process - RTS Books</title>
      </Helmet>
      <style>{`
        * {
          box-sizing: border-box;
        }

        @keyframes processFadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes processSlideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes processShake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }

        .process-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
          background-size: 400% 400%;
          animation: processFadeIn 0.6s ease;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        .process-wrapper {
          max-width: 1200px;
          margin: 0 auto;
        }

        .process-header {
          text-align: center;
          margin-bottom: 30px;
          color: white;
        }

        .process-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 10px;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
        }

        .process-card {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 30px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          margin-bottom: 20px;
        }

        .process-search-bar {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .process-search-input {
          flex: 1;
          min-width: 200px;
          padding: 12px 16px;
          border: 2px solid #e1e5e9;
          border-radius: 12px;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .process-search-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
        }

        .process-btn {
          padding: 12px 24px;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .process-btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
        }

        .process-btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
        }

        .process-btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .process-table-wrapper {
          overflow-x: auto;
          border-radius: 12px;
        }

        .process-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 12px;
          overflow: hidden;
        }

        .process-table thead {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .process-table th {
          padding: 16px;
          text-align: left;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .process-table td {
          padding: 16px;
          border-bottom: 1px solid #e1e5e9;
        }

        .process-table tbody tr:hover {
          background: #f8f9fa;
        }

        .process-table tbody tr:last-child td {
          border-bottom: none;
        }

        .process-action-btn {
          padding: 8px 16px;
          border: none;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-right: 8px;
        }

        .process-btn-edit {
          background: #10b981;
          color: white;
        }

        .process-btn-edit:hover {
          background: #059669;
          transform: translateY(-1px);
        }

        .process-btn-delete {
          background: #ef4444;
          color: white;
        }

        .process-btn-delete:hover {
          background: #dc2626;
          transform: translateY(-1px);
        }

        .process-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          backdrop-filter: blur(4px);
        }

        .process-modal-content {
          background: white;
          border-radius: 20px;
          padding: 30px;
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          animation: processSlideIn 0.3s ease;
        }

        .process-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .process-modal-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
        }

        .process-modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #64748b;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .process-modal-close:hover {
          background: #f1f5f9;
          color: #1e293b;
        }

        .process-form-group {
          margin-bottom: 20px;
        }

        .process-label {
          display: block;
          margin-bottom: 8px;
          color: #334155;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .process-label.required::after {
          content: " *";
          color: #dc2626;
        }

        .process-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e1e5e9;
          border-radius: 12px;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .process-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
        }

        .process-input.error {
          border-color: #dc2626;
          animation: processShake 0.5s ease;
        }

        .process-error {
          color: #dc2626;
          font-size: 0.85rem;
          margin-top: 6px;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .process-modal-actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
        }

        .process-btn-secondary {
          background: #e1e5e9;
          color: #334155;
        }

        .process-btn-secondary:hover {
          background: #cbd5e1;
        }

        .process-btn-toggle {
          background: #10b981;
          color: white;
        }

        .process-btn-toggle:hover {
          background: #059669;
        }

        .process-user-badge {
          display: inline-block;
          padding: 4px 8px;
          background: #e0e7ff;
          color: #4338ca;
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .process-loading {
          text-align: center;
          padding: 40px;
          color: #64748b;
        }

        .process-empty {
          text-align: center;
          padding: 40px;
          color: #64748b;
        }

        .process-toast {
          position: fixed;
          top: 20px;
          right: -300px;
          background: #fff;
          color: #333;
          padding: 15px 20px;
          border-radius: 12px;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          z-index: 10000;
          transition: all 0.3s ease;
          max-width: 350px;
          font-weight: 500;
        }

        .process-toast.process-toast-success {
          border-left: 4px solid #059669;
        }

        .process-toast.process-toast-error {
          border-left: 4px solid #dc2626;
        }

        .process-toast.process-toast-show {
          right: 20px;
          animation: processSlideIn 0.3s ease;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .process-container {
            padding: 15px;
          }

          .process-title {
            font-size: 2rem;
          }

          .process-card {
            padding: 20px;
            border-radius: 16px;
          }

          .process-search-bar {
            flex-direction: column;
          }

          .process-search-input {
            min-width: 100%;
          }

          .process-table {
            font-size: 0.9rem;
          }

          .process-table th,
          .process-table td {
            padding: 12px 8px;
          }

          .process-action-btn {
            padding: 6px 12px;
            font-size: 0.85rem;
            margin-right: 4px;
            margin-bottom: 4px;
          }

          .process-modal-content {
            padding: 20px;
            margin: 10px;
          }

          .process-modal-title {
            font-size: 1.3rem;
          }
        }

        @media (max-width: 480px) {
          .process-title {
            font-size: 1.5rem;
          }

          .process-card {
            padding: 15px;
          }

          .process-table {
            font-size: 0.85rem;
          }

          .process-table th,
          .process-table td {
            padding: 10px 6px;
          }

          .process-table th:nth-child(3),
          .process-table td:nth-child(3) {
            display: none;
          }

          .process-action-btn {
            padding: 6px 10px;
            font-size: 0.8rem;
          }

          .process-modal-content {
            padding: 15px;
          }

          .process-btn {
            padding: 10px 16px;
            font-size: 0.9rem;
          }
        }

        @media (max-width: 360px) {
          .process-table th,
          .process-table td {
            padding: 8px 4px;
            font-size: 0.8rem;
          }

          .process-action-btn {
            padding: 4px 8px;
            font-size: 0.75rem;
          }
        }
      `}</style>

      <div className="process-container">
        <div className="process-wrapper">
          <div className="process-header">
            <h1 className="process-title">Process Management</h1>
          </div>

          <div className="process-card">
            <div className="process-search-bar">
              <input
                type="text"
                className="process-search-input"
                placeholder="Search processes by name..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />
              {user && (
                <button
                  className="process-btn process-btn-toggle"
                  onClick={handleToggleView}
                >
                  {showMyProcesses ? "Show All" : "Show My Processes"}
                </button>
              )}
              <button
                className="process-btn process-btn-primary"
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
              >
                + Add Process
              </button>
            </div>

            {loading && <div className="process-loading">Loading...</div>}

            {!loading && processes.length === 0 && (
              <div className="process-empty">No processes found</div>
            )}

            {!loading && processes.length > 0 && (
              <div className="process-table-wrapper">
                <table className="process-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Amount</th>
                      <th>Created By</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {processes.map((process) => (
                      <tr key={process._id}>
                        <td>{process.name}</td>
                        <td>₹{parseFloat(process.amount).toFixed(2)}</td>
                        <td>
                          {process.user ? (
                            <span className="process-user-badge">
                              {process.user.name || process.user.phone || "Unknown"}
                            </span>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td>
                          {new Date(process.createdAt).toLocaleDateString()}
                        </td>
                        <td>
                          <button
                            className="process-action-btn process-btn-edit"
                            onClick={() => handleEdit(process)}
                          >
                            Edit
                          </button>
                          <button
                            className="process-action-btn process-btn-delete"
                            onClick={() => handleDelete(process._id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="process-modal" onClick={() => setShowModal(false)}>
          <div
            className="process-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="process-modal-header">
              <h2 className="process-modal-title">
                {isEditing ? "Edit Process" : "Add New Process"}
              </h2>
              <button
                className="process-modal-close"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="process-form-group">
                <label className="process-label required" htmlFor="name">
                  Process Name
                </label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("name")}
                  placeholder="Enter process name"
                  className={`process-input ${
                    errors.name && touched.name ? "error" : ""
                  }`}
                  disabled={loading}
                />
                {errors.name && touched.name && (
                  <div className="process-error">⚠️ {errors.name}</div>
                )}
              </div>

              <div className="process-form-group">
                <label className="process-label required" htmlFor="amount">
                  Amount
                </label>
                <input
                  id="amount"
                  type="text"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur("amount")}
                  placeholder="Enter amount (e.g., 100.50)"
                  className={`process-input ${
                    errors.amount && touched.amount ? "error" : ""
                  }`}
                  disabled={loading}
                />
                {errors.amount && touched.amount && (
                  <div className="process-error">⚠️ {errors.amount}</div>
                )}
              </div>

              <div className="process-modal-actions">
                <button
                  type="button"
                  className="process-btn process-btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="process-btn process-btn-primary"
                  disabled={loading}
                >
                  {loading ? "Saving..." : isEditing ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Process;

